import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { CarnivalStatusBadge } from "../components/CarnivalStatusBadge";
import { CarnivalHeroDecor, RibbonDivider } from "../components/CarnivalDecor";
import { CarnivalEventActions } from "../components/CarnivalEventActions";
import { ShareButton } from "../components/ShareButton";
import { LeaderboardAd } from "../components/AdSlot";
import {
  CARNIVAL,
  CARNIVAL_ISLAND_LABELS,
  CARNIVAL_UI,
  CARNIVAL_LAST_UPDATED,
  MARQUEE_EVENTS,
  formatCarnivalDate,
} from "../lib/carnivalMeta";
import { useSeoTags } from "../lib/useSeoTags";

// URL slug → internal slug. Mirrors the CruiseSchedulePage convention so
// that both `stmaarten` (canonical) and `st-maarten` (legacy) resolve to
// the same island. Anything else falls through to a 404 branch below.
const URL_TO_SLUG = {
  aruba: "aruba",
  curacao: "curacao",
  bonaire: "bonaire",
  stmaarten: "stmaarten",
  "st-maarten": "stmaarten",
};

const SITE = "https://islandsdaily.com";

// Reverse index: (islandKey, eventKey) → marquee slug. Built once at
// module load so every EventRow render is a plain lookup. If an event
// has a dedicated /carnival/{slug} page, its row surfaces a "See full
// event page →" link — otherwise the row is inert.
const MARQUEE_INDEX = Object.values(MARQUEE_EVENTS).reduce((acc, m) => {
  acc[`${m.islandKey}:${m.eventKey}`] = m.slug;
  return acc;
}, {});

const readStoredLang = () => {
  try {
    const s = localStorage.getItem("ir_lang");
    if (s === "en" || s === "nl" || s === "es" || s === "pap" || s === "pap_aw") return s;
  } catch (_e) { /* ignore private-mode */ }
  return "en";
};

// One event row in the schedule table. Kept as a leaf so the table stays
// legible in the parent; also lets us memoise the badge cheaply. When a
// dedicated marquee page exists for this event, the row appends a
// "See full event page →" link so the island hub feeds discovery.
const EventRow = ({ event, lang, islandKey, showActions }) => {
  const label = event.labels[lang] || event.labels.en;
  const about = event.about[lang] || event.about.en;
  const displayDate = event.date
    ? formatCarnivalDate(event.date, lang)
    : (CARNIVAL_UI.date_tbd[lang] || CARNIVAL_UI.date_tbd.en);
  const marqueeSlug = MARQUEE_INDEX[`${islandKey}:${event.key}`];
  const statusBadge = (
    <CarnivalStatusBadge
      status={event.status}
      source={event.status === "confirmed" ? event.confirmed_by : null}
      lang={lang}
    />
  );
  // Responsive layout: on `<md` the row becomes a stacked block card so the
  // description gets full viewport width instead of fighting for space in
  // a squeezed table column. On `md+` we keep the classic 3-column table
  // (date | description | status) via `md:table-row` / `md:table-cell`.
  // Status badge is rendered twice but only one is ever visible thanks to
  // `md:hidden` / `hidden md:table-cell`.
  return (
    <tr
      className="block md:table-row border-b border-[#F5F5F5] py-4 md:py-0 md:align-top"
      data-testid={`carnival-event-${event.key}`}
    >
      <td className="block md:table-cell md:py-4 md:pr-4 md:whitespace-nowrap">
        <div className="flex flex-col items-start gap-1.5 md:block">
          <span className="text-[13px] text-[#1A2E35] font-medium whitespace-nowrap">
            {displayDate}
          </span>
          {/* Mobile-only: status badge stacked under date so its long
              "Estimated · Pending official schedule" tail doesn't
              overflow the viewport at 390vw. */}
          <span className="md:hidden">{statusBadge}</span>
        </div>
      </td>
      <td className="block md:table-cell mt-2 md:mt-0 md:py-4 md:pr-4">
        <div className="text-[15px] font-medium text-[#1A2E35] mb-1">{label}</div>
        <div className="text-[12.5px] text-[#3D4C52] leading-[1.6] max-w-[560px] space-y-2">
          {/* Render copy paragraphs — split on double-newline so events
              whose `about` copy has been restructured into 2-3 short
              paragraphs (Part 3 sweep, 2026-02) render as distinct
              blocks. Events whose copy is still one unbroken string
              render as one paragraph, no visual change. Filters empty
              splits so a trailing "\n\n" doesn't emit an empty p. */}
          {about
            .split(/\n{2,}/)
            .map((p) => p.trim())
            .filter(Boolean)
            .map((para, i) => (
              <p key={i}>{para}</p>
            ))}
        </div>
        {event.location?.name ? (
          <div className="text-[11.5px] text-[#6B7C82] mt-1.5">
            📍 {event.location.name}
          </div>
        ) : null}
        {marqueeSlug && (
          <Link
            to={`/carnival/${marqueeSlug}`}
            className="inline-flex items-center gap-1 text-[12px] text-[#0AADA0] hover:underline mt-2"
            data-testid={`carnival-event-marquee-link-${event.key}`}
          >
            See full event page <ExternalLink size={11} />
          </Link>
        )}
        {showActions && (
          <CarnivalEventActions event={event} islandKey={islandKey} lang={lang} />
        )}
      </td>
      {/* Desktop-only status column; on mobile the badge above the
          description already covers this. */}
      <td className="hidden md:table-cell md:py-4 md:pr-2 md:whitespace-nowrap">
        {statusBadge}
      </td>
    </tr>
  );
};

// Build the Festival + subEvent JSON-LD graph. Confirmed events emit a
// full Event child (startDate, location, organizer, offers, eventStatus,
// sameAs). Estimated events are intentionally left out of the schema so
// we don't tell Google an unconfirmed date is a real scheduled event.
// (Google's Rich Results Test flags Event schema on estimated/tentative
// dates as misleading — better to have zero schema for those than to
// mark them "EventPostponed" without an original date.)
const buildFestivalSchema = (islandKey, lang) => {
  const p = CARNIVAL[islandKey];
  if (!p) return null;
  const meta = p.meta[lang] || p.meta.en;
  const canonical = `${SITE}/${islandKey}/carnival`;
  const confirmed = p.events.filter((e) => e.status === "confirmed" && e.date);
  const subEvents = confirmed.map((e) => {
    const label = e.labels[lang] || e.labels.en;
    return {
      "@type": "Event",
      name: `${label} — ${CARNIVAL_ISLAND_LABELS[islandKey]} Carnival 2027`,
      startDate: `${e.date}T${e.time_local || "12:00"}:00${p.timezone}`,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: e.location?.name || CARNIVAL_ISLAND_LABELS[islandKey],
        address: {
          "@type": "PostalAddress",
          addressLocality: e.location?.city || CARNIVAL_ISLAND_LABELS[islandKey],
          addressCountry: islandKey === "stmaarten" ? "SX" :
                          islandKey === "aruba"    ? "AW" :
                          islandKey === "curacao"  ? "CW" :
                          /* bonaire */              "BQ",
        },
      },
      organizer: {
        "@type": "Organization",
        name: p.organizer.name,
        url: p.organizer.url,
      },
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: islandKey === "stmaarten" ? "ANG" : "AWG",
        availability: "https://schema.org/InStock",
        url: canonical,
      },
      image: [`${SITE}/og.png`],
      description: e.about[lang] || e.about.en,
    };
  });
  return {
    "@context": "https://schema.org",
    "@type": "Festival",
    name: meta.h1,
    description: meta.description,
    startDate: p.season.start,
    endDate: p.season.end,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: CARNIVAL_ISLAND_LABELS[islandKey],
      address: {
        "@type": "PostalAddress",
        addressCountry: islandKey === "stmaarten" ? "SX" :
                        islandKey === "aruba"    ? "AW" :
                        islandKey === "curacao"  ? "CW" :
                        /* bonaire */              "BQ",
      },
    },
    organizer: {
      "@type": "Organization",
      name: p.organizer.name,
      url: p.organizer.url,
    },
    image: [`${SITE}/og.png`],
    sameAs: p.sameAs,
    url: canonical,
    dateModified: CARNIVAL_LAST_UPDATED,
    subEvent: subEvents,
  };
};

// FAQ JSON-LD is emitted for every language so Papiamento readers get
// language-appropriate rich results — Google respects the FAQ's own
// inLanguage marker and this is one of the few Papiamento SEO
// opportunities where no competitor currently plays.
const buildFaqSchema = (islandKey, lang) => {
  const p = CARNIVAL[islandKey];
  if (!p?.faq?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: lang === "pap_aw" ? "pap" : lang,
    mainEntity: p.faq.map((f) => ({
      "@type": "Question",
      name: f.q[lang] || f.q.en,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a[lang] || f.a.en,
      },
    })),
  };
};

export default function CarnivalIslandPage() {
  const { islandSlug } = useParams();
  const slug = URL_TO_SLUG[islandSlug] || null;
  const [lang, setLang] = useState(() => readStoredLang());
  const [sponsorAd, setSponsorAd] = useState(null);

  useEffect(() => {
    if (!slug) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const ctrl = new AbortController();
    fetch(`${backend}/api/ads/carnival?page_type=carnival_hub&island=${slug}`, {
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.ad) {
          setSponsorAd(data.ad);
          // Client-side impression ping (redundant with server-side write
          // inside /ads/carnival — belt+braces against ad-blockers). Fire
          // and forget; failures are silent per the tracking best-effort spec.
          fetch(`${backend}/api/ads/track/impression`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              slot: "carnival_hub_leaderboard",
              page_type: "carnival_hub",
              island: slug,
              sponsor_id: data.ad.sponsor_id || "",
              sponsor_name: data.ad.sponsor_name || "",
            }),
          }).catch(() => {});
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [slug]);

  useEffect(() => {
    const onLangChange = () => setLang(readStoredLang());
    window.addEventListener("ir_lang_changed", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("ir_lang_changed", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);
  const handleLangChange = useCallback((next) => {
    try {
      localStorage.setItem("ir_lang", next);
      window.dispatchEvent(new Event("ir_lang_changed"));
    } catch (_e) { /* ignore quota / private-mode */ }
    setLang(next);
  }, []);

  const payload = slug ? CARNIVAL[slug] : null;

  const seoConfigs = useMemo(() => {
    if (!slug || !payload) return {};
    const canonical = `${SITE}/${slug}/carnival`;
    const alternates = [
      { hreflang: "en",        href: canonical },
      { hreflang: "nl",        href: canonical },
      { hreflang: "es",        href: canonical },
      { hreflang: "pap",       href: canonical },
      { hreflang: "x-default", href: canonical },
    ];
    return {
      en:     { ...payload.meta.en,     canonical, alternates },
      nl:     { ...payload.meta.nl,     canonical, alternates },
      es:     { ...payload.meta.es,     canonical, alternates },
      pap:    { ...(payload.meta.pap    || payload.meta.en), canonical, alternates },
      pap_aw: { ...(payload.meta.pap_aw || payload.meta.en), canonical, alternates },
    };
  }, [slug, payload]);
  useSeoTags(lang, seoConfigs);

  if (!slug || !payload) {
    return (
      <div className="min-h-screen bg-white p-8" data-testid="carnival-404">
        <p>
          {CARNIVAL_UI.unknown_island[lang] || CARNIVAL_UI.unknown_island.en}{" "}
          <Link to="/carnival" className="text-[#0AADA0]">
            ← {CARNIVAL_UI.all_carnivals_link[lang] || CARNIVAL_UI.all_carnivals_link.en}
          </Link>
        </p>
      </div>
    );
  }

  const meta = payload.meta[lang] || payload.meta.en;
  const intro = payload.intro[lang] || payload.intro.en;
  const festivalSchema = buildFestivalSchema(slug, lang);
  const faqSchema = buildFaqSchema(slug, lang);

  return (
    <div className="min-h-screen bg-white" data-testid={`carnival-island-${slug}`}>
      {festivalSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(festivalSchema) }}
          data-testid="carnival-festival-schema"
        />
      )}
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          data-testid="carnival-faq-schema"
        />
      )}

      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/carnival"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="carnival-back-link"
          >
            <ArrowLeft size={15} /> {CARNIVAL_UI.back_all_carnivals[lang] || CARNIVAL_UI.back_all_carnivals.en}
          </Link>
          <Link to="/" className="ml-auto font-serif-display text-[18px] leading-none" data-testid="carnival-home-link">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          <LanguageToggle lang={lang} onChange={handleLangChange} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Phase C rollout (2026-08-10) — composition applied to all 4
            island hubs. Sponsor slot fetches its creative live from
            /api/ads/carnival; empty-shell rendered when no active
            in-window sponsor exists for the (page_type, island) tuple. */}
        <div
          className="mb-6"
          data-testid="carnival-hub-sponsor-slot"
          aria-label={`${CARNIVAL_UI.sponsored_label[lang] || CARNIVAL_UI.sponsored_label.en} placement`}
        >
          <LeaderboardAd
            ad={sponsorAd}
            onAdClick={() => {
              const backend = process.env.REACT_APP_BACKEND_URL;
              if (!backend || !sponsorAd) return;
              fetch(`${backend}/api/ads/track/click`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  slot: "carnival_hub_leaderboard",
                  page_type: "carnival_hub",
                  island: slug,
                  sponsor_id: sponsorAd.sponsor_id || "",
                  sponsor_name: sponsorAd.sponsor_name || "",
                }),
              }).catch(() => {});
            }}
          />
        </div>

        <div
          className="relative overflow-hidden rounded-2xl border border-[#F0EDE3] bg-white p-6 sm:p-8 mb-8"
          data-testid="carnival-hub-hero"
        >
          <CarnivalHeroDecor />
          <div className="relative">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h1
                className="font-serif-display text-3xl sm:text-4xl text-[#1A2E35] mb-3"
                data-testid="carnival-h1"
              >
                {meta.h1}
              </h1>
              <div data-testid="carnival-hub-share">
                <ShareButton
                  title={meta.h1}
                  text={intro?.slice(0, 160) || meta.h1}
                  url={`https://islandsdaily.com/${slug}/carnival?ref=cshare`}
                  testId="carnival-hub-share-button"
                />
              </div>
            </div>
            <p className="text-[15px] text-[#3D4C52] leading-[1.7] max-w-3xl mb-2">
              {intro}
            </p>
            <div className="text-[11px] text-[#6B7C82]" data-testid="carnival-last-updated">
              {CARNIVAL_UI.last_updated[lang] || CARNIVAL_UI.last_updated.en}: {CARNIVAL_LAST_UPDATED}
            </div>
          </div>
        </div>

        {/* Events & Dates */}
        <section className="mb-12">
          <h2
            className="text-[11px] uppercase tracking-wider text-[#0AADA0] font-semibold mb-3"
            data-testid="carnival-events-heading"
          >
            {CARNIVAL_UI.section_events[lang] || CARNIVAL_UI.section_events.en}
          </h2>
          <div className="overflow-x-auto" data-testid="carnival-events-table">
            <table className="w-full text-left border-collapse block md:table">
              <tbody className="block md:table-row-group">
                {[...payload.events]
                  .sort((a, b) => {
                    if (!a.date && !b.date) return 0;
                    if (!a.date) return 1;
                    if (!b.date) return -1;
                    return a.date.localeCompare(b.date);
                  })
                  .map((e) => (
                    <EventRow
                      key={e.key}
                      event={e}
                      lang={lang}
                      islandKey={slug}
                      showActions
                    />
                  ))}
              </tbody>
            </table>
          </div>
        </section>

        <RibbonDivider />

        {/* Organizer */}
        <section className="mb-10 text-[13px] text-[#3D4C52]" data-testid="carnival-organizer">
          <span className="uppercase tracking-wider text-[10px] text-[#6B7C82] mr-2">
            {CARNIVAL_UI.section_organizer[lang] || CARNIVAL_UI.section_organizer.en}:
          </span>
          <a
            href={payload.organizer.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0AADA0] hover:underline"
          >
            {payload.organizer.name}
          </a>
        </section>

        {/* FAQ */}
        {payload.faq?.length ? (
          <section className="max-w-3xl border-t border-[#EEEEEE] pt-8" data-testid="carnival-faq">
            <h2 className="font-serif-display text-xl sm:text-2xl text-[#1A2E35] mb-4">
              {CARNIVAL_UI.section_faq[lang] || CARNIVAL_UI.section_faq.en}
            </h2>
            <div className="flex flex-col gap-5">
              {payload.faq.map((f, i) => (
                <div key={i} data-testid={`carnival-faq-item-${i}`}>
                  <div className="text-[15px] font-medium text-[#1A2E35] mb-1.5">
                    {f.q[lang] || f.q.en}
                  </div>
                  <div className="text-[14px] text-[#3D4C52] leading-[1.7]">
                    {f.a[lang] || f.a.en}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
